// src/NewUPT/ClockInService/DoctorClockService.js
import apiBaseUrl from '../../utils/config.js';

class DoctorClockService {
  constructor() {
    this.baseUrl = `${apiBaseUrl}/doctor-clock`;
  }

  getToken() {
    return localStorage.getItem("jwt");
  }

  getAuthHeaders() {
    return {
      'Authorization': `Bearer ${this.getToken()}`,
      'Content-Type': 'application/json',
    };
  }

  async getClockStatus(doctorId) {
    const response = await fetch(`${this.baseUrl}/status/${doctorId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch clock status');
    }
    return data;
  }

  /**
   * Clock in with optional coordinates
   * coords = { latitude: number, longitude: number }
   */
  async clockIn(doctorId, coords = null) {
    return this._post(`/clock-in/${doctorId}`, coords);
  }

  /**
   * Clock out with optional coordinates
   * coords = { latitude: number, longitude: number }
   */
  async clockOut(doctorId, coords = null) {
    return this._post(`/clock-out/${doctorId}`, coords);
  }

  /**
   * Start break with optional coordinates
   * coords = { latitude: number, longitude: number }
   */
  async startBreak(doctorId, coords = null) {
    // backend mapping: @PostMapping("/pause/{doctorId}")
    return this._post(`/pause/${doctorId}`, coords);
  }

  /**
   * End break with optional coordinates
   * coords = { latitude: number, longitude: number }
   */
  async endBreak(doctorId, coords = null) {
    // backend mapping: @PostMapping("/resume/{doctorId}")
    return this._post(`/resume/${doctorId}`, coords);
  }

  /**
   * Internal helper to POST with optional JSON body
   */
  async _post(path, body = null) {
    const options = {
      method: 'POST',
      headers: this.getAuthHeaders(),
      // If body is null, send an empty object so backend @RequestBody(required=false) is happy
      body: JSON.stringify(body || {})
    };

    const response = await fetch(`${this.baseUrl}${path}`, options);

    const contentType = response.headers.get('content-type');

    let responseData;
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text(); // fallback to plain text
    }

    if (!response.ok) {
      const errorMsg =
        (responseData && (responseData.error || responseData.message)) ||
        'API error';
      throw new Error(errorMsg);
    }

    return responseData;
  }

  async getTodayLog(doctorId) {
    const response = await fetch(`${this.baseUrl}/logs/today/${doctorId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch today’s log');
    }

    return data;
  }

  // 🔹 Weekly summary for manager/admin timecard
  async getWeekSummary(weekStart) {
    const response = await fetch(
      `${this.baseUrl}/week-summary?weekStart=${weekStart}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch week summary');
    }
    return data; // List<DoctorWeekSummaryDTO>
  }

  // 🔹 Edit a specific clock log (admin/manager only)
  async editLog(logId, payload) {
    const response = await fetch(`${this.baseUrl}/edit-log/${logId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    const text = await response.text();
    if (!response.ok) {
      throw new Error(text || 'Failed to edit log');
    }
    return text;
  }
}

export default DoctorClockService;
